import { Trans, msg } from '@lingui/macro';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { Box, Flex, Text } from '~/design-system';
import {
  ProgressIndicator as BaseProgressIndicator,
  ProgressStep,
} from '../../../../components/Layout/ProgressIndicator';
import { getStepLinks } from '../../../_config';
import NotFound from '../../404';
import { useCommonLinks } from '../../_config';
import Collection from './collection';
import Complete from './complete';
import MapFields from './mapFields';
import MapHeader from './mapHeader';
import Name from './name';
import Ready from './ready';
import SendNotification from './sendNotification';
import Suppliers from './suppliers/_config';
import Validation from './validation';

export function $ImportIdRoutes({ match }) {
  return (
    <Switch>
      {/* The index route: */}
      <Redirect exact from={`${match.path}`} to={`${match.url}/mapHeader`} />

      <Route path={`${match.path}/name`} component={Name} />
      <Route path={`${match.path}/mapHeader`} component={MapHeader} />
      <Route path={`${match.path}/mapFields`} component={MapFields} />
      <Route path={`${match.path}/validation`} component={Validation} />
      <Route path={`${match.path}/suppliers`} component={Suppliers} />
      <Route path={`${match.path}/collection`} component={Collection} />
      <Route path={`${match.path}/ready`} component={Ready} />
      <Route path={`${match.path}/complete`} component={Complete} />
      <Route
        path={`${match.path}/sendNotification`}
        component={SendNotification}
      />

      <Route component={NotFound} />
    </Switch>
  );
}

/** @typedef {keyof ReturnType<getProductMasterSyncLinks>} Step The possible steps for productMasterListSync. */

export const importType = 'productMasterListSync';

/**
 * @param {object} params
 * @param {string} params.workspaceId
 * @param {string} params.importId
 * @returns
 */
export function getProductMasterSyncLinks({ workspaceId, importId }) {
  const common = useCommonLinks();
  const baseLink = `/${workspaceId}`;
  const baseImportId = importId;
  const links = {
    root: common.root,
    uploadFile: () => `${baseLink}/${importType}/uploadFile`,
    /**
     * We might not have set the importId yet if we've just uploaded a new file. We may pass an importId in that case.
     * @param {{ importId?: string}} [options]
     */
    name: (options) =>
      `${baseLink}/${importType}/${options?.importId || baseImportId}/name`,
    mapHeader: () => `${baseLink}/${importType}/${importId}/mapHeader`,
    mapFields: () => `${baseLink}/${importType}/${importId}/mapFields`,
    validation: () => `${baseLink}/${importType}/${importId}/validation`,
    suppliers: ({ tab } = { tab: 'all' }) =>
      `${baseLink}/${importType}/${importId}/suppliers/${tab}`,
    /** @param {{ supplierName?: string}} [options] */
    supplier: (options) => {
      return `${baseLink}/${importType}/${importId}/suppliers/${options?.supplierName}`;
    },
    collection: () =>
      `${baseLink}/productMasterListSync/${importId}/collection`,
    ready: () => `${baseLink}/productMasterListSync/${importId}/ready`,
    complete: () => `${baseLink}/productMasterListSync/${importId}/complete`,
    partners: () => `${baseLink}/productMasterListSync/${importId}/partners`,
    sendNotification: () =>
      `${baseLink}/productMasterListSync/${importId}/sendNotification`,
  };
  return links;
}

/**
 * @param {Step} currentStep
 * @returns
 */
export function useLink(currentStep) {
  /** @type {{importId: string}} */
  const { workspaceId, importId } = useParams();
  const urls = getProductMasterSyncLinks({ workspaceId, importId });
  return getStepLinks(
    urls,
    // supplier its just a substep of suppliers.
    Object.keys(urls).filter((key) => key !== 'supplier'),
    currentStep
  );
}

export function ProgressIndicator({ step }) {
  const steps = [
    'uploadFile',
    'mapData',
    'matchSuppliers',
    'collection',
    'ready',
  ];
  const currentIndex = steps.indexOf(step);
  const currentStep = currentIndex + 1;

  return (
    <Flex sx={{ justifyContent: 'space-between', paddingX: 40 }}>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'left' }}>
        <Text>
          <Trans>Master list sync</Trans>
        </Text>
      </Box>
      <BaseProgressIndicator currentIndex={currentIndex}>
        <ProgressStep>
          <Trans>Upload file</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Map data</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Match suppliers</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Choose collection</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Confirm</Trans>
        </ProgressStep>
      </BaseProgressIndicator>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'right' }}>
        <Text>
          Step {currentStep} of {steps.length}
        </Text>
      </Box>
    </Flex>
  );
}

export const fieldsToImport = [
  {
    key: 'product.requestedName',
    label: msg`Requested product name`,
    required: true,
  },
  { key: 'exhibitor.name', label: msg`Supplier name`, required: true },
  { key: 'product.externalId', label: msg`External ID`, required: true },
  { key: 'product.inventoryId', label: msg`Inventory ID` },
  { key: 'product.requestedEanBottle', label: msg`Product EAN` },
  { key: 'product.requestedVintage', label: msg`Product vintage` },
  { key: 'product.instanceId', label: msg`Bottlebooks Product ID` },
];
